/**
 * PassportBackCover - Contraportada del pasaporte
 * Mismo fondo premium que la portada (cuero, texturas, marco) con cita y estadísticas.
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import Leather from "../../assets/passport/leather.png";

const PassportBackCover = ({ countriesCount }) => {
	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				background: "linear-gradient(170deg, #0f1a2e 0%, #162240 35%, #1a2a4e 65%, #111d35 100%)",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				position: "relative",
				overflow: "hidden",
				fontFamily: "'Playfair Display', serif",
			}}
		>
			{/* Imagen de cuero como fondo (igual que portada) */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					backgroundImage: `url(${Leather})`,
					backgroundSize: "cover",
					backgroundPosition: "center",
					backgroundRepeat: "no-repeat",
					opacity: 0.2,
					pointerEvents: "none",
				}}
			/>

			{/* Capa 1: grano fino de piel */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					opacity: 0.12,
					backgroundImage: `
						radial-gradient(circle at 2px 2px, rgba(255,255,255,0.08) 1px, transparent 1px),
						radial-gradient(circle at 5px 7px, rgba(0,0,0,0.12) 0.5px, transparent 0.5px),
						radial-gradient(circle at 9px 3px, rgba(255,255,255,0.06) 0.8px, transparent 0.8px)
					`,
					backgroundSize: "11px 11px, 13px 13px, 7px 9px",
					pointerEvents: "none",
				}}
			/>

			{/* Capa 2: micro-arrugas de piel */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					opacity: 0.06,
					backgroundImage: `
						repeating-linear-gradient(2deg, transparent, transparent 3px, rgba(255,255,255,0.03) 3px, rgba(255,255,255,0.03) 4px),
						repeating-linear-gradient(178deg, transparent, transparent 5px, rgba(0,0,0,0.05) 5px, rgba(0,0,0,0.05) 6px)
					`,
					pointerEvents: "none",
				}}
			/>

			{/* Capa 3: textura de poro de cuero */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					opacity: 0.04,
					backgroundImage: `
						radial-gradient(ellipse at 30% 20%, rgba(0,0,0,0.3) 0%, transparent 0.8%),
						radial-gradient(ellipse at 70% 40%, rgba(0,0,0,0.25) 0%, transparent 0.6%),
						radial-gradient(ellipse at 20% 60%, rgba(0,0,0,0.2) 0%, transparent 0.7%),
						radial-gradient(ellipse at 80% 80%, rgba(0,0,0,0.3) 0%, transparent 0.5%),
						radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.15) 0%, transparent 1%)
					`,
					backgroundSize: "23px 19px, 17px 21px, 29px 23px, 19px 17px, 31px 27px",
					pointerEvents: "none",
				}}
			/>

			{/* Capa 4: ruido de cuero */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					opacity: 0.035,
					backgroundImage: `
						conic-gradient(from 0deg at 35% 25%, rgba(255,255,255,0.1) 0deg, transparent 30deg, transparent 330deg, rgba(255,255,255,0.1) 360deg),
						conic-gradient(from 120deg at 65% 75%, rgba(255,255,255,0.08) 0deg, transparent 25deg, transparent 335deg, rgba(255,255,255,0.08) 360deg),
						conic-gradient(from 240deg at 50% 50%, rgba(0,0,0,0.1) 0deg, transparent 20deg, transparent 340deg, rgba(0,0,0,0.1) 360deg)
					`,
					backgroundSize: "40px 40px, 35px 45px, 50px 38px",
					pointerEvents: "none",
				}}
			/>

			{/* Capa 5: brillo sutil de cuero */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					background: `
						radial-gradient(ellipse at 50% 38%, rgba(201,168,76,0.04) 0%, transparent 55%),
						radial-gradient(ellipse at 30% 25%, rgba(255,255,255,0.015) 0%, transparent 40%),
						radial-gradient(ellipse at 70% 65%, rgba(0,0,0,0.08) 0%, transparent 50%)
					`,
					pointerEvents: "none",
				}}
			/>

			{/* Capa 6: bordes oscurecidos */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					background: `
						linear-gradient(to right, rgba(0,0,0,0.15) 0%, transparent 8%, transparent 92%, rgba(0,0,0,0.15) 100%),
						linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, transparent 6%, transparent 94%, rgba(0,0,0,0.1) 100%)
					`,
					pointerEvents: "none",
				}}
			/>

			{/* Marco exterior dorado (igual que portada) */}
			<Box
				sx={{
					position: "absolute",
					inset: "14px",
					border: "1.5px solid rgba(201, 168, 76, 0.2)",
					borderRadius: "8px",
					pointerEvents: "none",
					boxShadow: "inset 0 0 20px rgba(0,0,0,0.15)",
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					inset: "18px",
					border: "0.5px solid rgba(201, 168, 76, 0.1)",
					borderRadius: "6px",
					pointerEvents: "none",
				}}
			/>

			{/* Esquinas filigrana (igual que portada) */}
			{["top-left", "top-right", "bottom-left", "bottom-right"].map((corner) => {
				const isTop = corner.includes("top");
				const isLeft = corner.includes("left");
				return (
					<Box
						key={corner}
						sx={{
							position: "absolute",
							[isTop ? "top" : "bottom"]: 24,
							[isLeft ? "left" : "right"]: 24,
							width: 32,
							height: 32,
							pointerEvents: "none",
						}}
					>
						<Box
							sx={{
								position: "absolute",
								[isTop ? "top" : "bottom"]: 0,
								[isLeft ? "left" : "right"]: 0,
								width: 32,
								height: "1px",
								background: `linear-gradient(${isLeft ? "90deg" : "270deg"}, rgba(201,168,76,0.45), transparent)`,
							}}
						/>
						<Box
							sx={{
								position: "absolute",
								[isTop ? "top" : "bottom"]: 0,
								[isLeft ? "left" : "right"]: 0,
								width: "1px",
								height: 32,
								background: `linear-gradient(${isTop ? "180deg" : "0deg"}, rgba(201,168,76,0.45), transparent)`,
							}}
						/>
						<Box
							sx={{
								position: "absolute",
								[isTop ? "top" : "bottom"]: -3,
								[isLeft ? "left" : "right"]: -3,
								width: 6,
								height: 6,
								transform: "rotate(45deg)",
								backgroundColor: "rgba(201,168,76,0.2)",
							}}
						/>
					</Box>
				);
			})}

			{/* Contenido central (cita, estadísticas, copyright) — tipografía alineada con portada para mejor legibilidad */}
			<Box sx={{ textAlign: "center", px: 4 }}>
				<Typography
					sx={{
						fontSize: "2.5rem",
						mb: 3,
						color: "rgba(201, 168, 76, 0.9)",
						filter: "drop-shadow(0 2px 8px rgba(201, 168, 76, 0.25))",
						textShadow: "0 1px 2px rgba(0,0,0,0.4), 0 0 8px rgba(0,0,0,0.2)",
					}}
				>
					✧
				</Typography>

				<Typography
					sx={{
						fontSize: { xs: "0.9rem", sm: "1.1rem" },
						color: "rgba(201, 168, 76, 0.9)",
						fontStyle: "italic",
						lineHeight: 1.8,
						mb: 1,
						maxWidth: 320,
						mx: "auto",
						textShadow: "0 1px 2px rgba(0,0,0,0.4), 0 0 8px rgba(0,0,0,0.2)",
					}}
				>
					"El mundo es un libro y aquellos que no viajan solo leen una página"
				</Typography>

				<Typography
					sx={{
						fontSize: "0.65rem",
						color: "rgba(201, 168, 76, 0.65)",
						letterSpacing: "0.1em",
						mb: 4,
						textShadow: "0 1px 2px rgba(0,0,0,0.4), 0 0 6px rgba(0,0,0,0.2)",
					}}
				>
					— San Agustín
				</Typography>

				<Box
					sx={{
						display: "flex",
						gap: 4,
						justifyContent: "center",
						mb: 4,
					}}
				>
					<Box sx={{ textAlign: "center" }}>
						<Typography
							sx={{
								fontSize: { xs: "1.8rem", sm: "2.2rem" },
								color: "#c9a84c",
								fontWeight: 700,
								lineHeight: 1,
								textShadow: "0 1px 0 rgba(201,168,76,0.3), 0 2px 4px rgba(0,0,0,0.3), 0 0 20px rgba(201,168,76,0.08)",
							}}
						>
							{countriesCount}
						</Typography>
						<Typography
							sx={{
								fontSize: "0.55rem",
								color: "rgba(201, 168, 76, 0.78)",
								textTransform: "uppercase",
								letterSpacing: "0.15em",
								mt: 0.5,
								textShadow: "0 1px 2px rgba(0,0,0,0.4), 0 0 6px rgba(0,0,0,0.2)",
							}}
						>
							{countriesCount === 1 ? "Destino" : "Destinos"}
						</Typography>
					</Box>
					<Box
						sx={{
							width: "1px",
							height: 40,
							backgroundColor: "rgba(201, 168, 76, 0.15)",
							alignSelf: "center",
						}}
					/>
					<Box sx={{ textAlign: "center" }}>
						<Typography
							sx={{
								fontSize: { xs: "1.8rem", sm: "2.2rem" },
								color: "#c9a84c",
								fontWeight: 700,
								lineHeight: 1,
								textShadow: "0 1px 0 rgba(201,168,76,0.3), 0 2px 4px rgba(0,0,0,0.3), 0 0 20px rgba(201,168,76,0.08)",
							}}
						>
							∞
						</Typography>
						<Typography
							sx={{
								fontSize: "0.55rem",
								color: "rgba(201, 168, 76, 0.78)",
								textTransform: "uppercase",
								letterSpacing: "0.15em",
								mt: 0.5,
								textShadow: "0 1px 2px rgba(0,0,0,0.4), 0 0 6px rgba(0,0,0,0.2)",
							}}
						>
							Recuerdos
						</Typography>
					</Box>
				</Box>

				<Box
					sx={{
						width: 60,
						height: "1px",
						background: "linear-gradient(90deg, transparent, rgba(201, 168, 76, 0.3), transparent)",
						mx: "auto",
						mb: 3,
					}}
				/>

				<Typography
					sx={{
						fontSize: "0.6rem",
						color: "rgba(201, 168, 76, 0.7)",
						letterSpacing: "0.15em",
						textTransform: "uppercase",
						textShadow: "0 1px 2px rgba(0,0,0,0.4), 0 0 6px rgba(0,0,0,0.2)",
					}}
				>
					Viajes Molones © {new Date().getFullYear()}
				</Typography>
			</Box>
		</Box>
	);
};

export default PassportBackCover;
